// CoachMeUpp 5D Leadership Development Model — canonical domain data.
// 5 phases × 3 competencies (mindset / behavior / outcome) = 15 competencies.
// Labels and questionnaire statements live in /src/i18n.

export const PHASES = [
  {
    key: "discover",
    comps: [
      { key: "self_awareness", dim: "mindset" },
      { key: "empathy", dim: "behavior" },
      { key: "team_dynamics", dim: "outcome" },
    ],
  },
  {
    key: "define",
    comps: [
      { key: "strategic_goal", dim: "mindset" },
      { key: "clear_communication", dim: "behavior" },
      { key: "goal_ownership", dim: "outcome" },
    ],
  },
  {
    key: "develop",
    comps: [
      { key: "coaching_skills", dim: "mindset" },
      { key: "creativity", dim: "behavior" },
      { key: "learning_culture", dim: "outcome" },
    ],
  },
  {
    key: "drive",
    comps: [
      { key: "action_orientation", dim: "mindset" },
      { key: "follow_through", dim: "behavior" },
      { key: "motivation", dim: "outcome" },
    ],
  },
  {
    key: "debrief",
    comps: [
      { key: "feedback", dim: "mindset" },
      { key: "self_evaluation", dim: "behavior" },
      { key: "lessons_learned", dim: "outcome" },
    ],
  },
];

export const COMPETENCIES = PHASES.flatMap((p) =>
  p.comps.map((c) => ({ ...c, phase: p.key }))
);

export const SCALE_MAX = 5;
export const NEUTRAL = 3;

export const TIER_MODELS = {
  "4-tier": [
    { key: "c_suite" },
    { key: "vp_director" },
    { key: "mid_manager" },
    { key: "junior_ic" },
  ],
  "6-tier": [
    { key: "executive" },
    { key: "vp" },
    { key: "director" },
    { key: "senior_manager" },
    { key: "team_lead" },
    { key: "associate" },
  ],
};

export const clamp = (n, min = 1, max = SCALE_MAX) => Math.max(min, Math.min(max, n));

export function emptyMatrix(modelType, value = NEUTRAL) {
  const matrix = {};
  TIER_MODELS[modelType].forEach((tier) => {
    const row = {};
    COMPETENCIES.forEach((c) => (row[c.key] = value));
    matrix[tier.key] = row;
  });
  return matrix;
}

export const scoreOf = (scores, tierKey, compKey) => scores?.[tierKey]?.[compKey] ?? NEUTRAL;

// 5D bands: 4–5 strong area · 3–3.9 improvement potential · 0–2.9 development area
export const bandKey = (v) => (v >= 4 ? "strong" : v >= 3 ? "potential" : "gap");

export const scoreColor = (v) => {
  const b = bandKey(v);
  return b === "strong" ? "#10B981" : b === "potential" ? "#F59E0B" : "#F43F5E";
};

export const avgOf = (vals) => (vals.length ? vals.reduce((a, b) => a + b, 0) / vals.length : 0);

export const tierAverage = (scores, tierKey) =>
  avgOf(COMPETENCIES.map((c) => scoreOf(scores, tierKey, c.key)));

export const phaseAverage = (scores, tierKey, phaseKey) => {
  const phase = PHASES.find((p) => p.key === phaseKey);
  return avgOf(phase.comps.map((c) => scoreOf(scores, tierKey, c.key)));
};

// 0–5 average -> 0–100 readiness index
export const readinessPct = (avg) => Math.round((avg / SCALE_MAX) * 100);

export const DIMENSIONS = ["mindset", "behavior", "outcome"];

export const dimensionAverage = (scores, tierKey, dim) =>
  avgOf(COMPETENCIES.filter((c) => c.dim === dim).map((c) => scoreOf(scores, tierKey, c.key)));

export const orgDimensionAverage = (scores, tiers, dim) =>
  avgOf(tiers.map((t) => dimensionAverage(scores, t.key, dim)));

// One row per assessed seniority level: phase averages + total + band + headcount.
export function cohortRows(scores, tiers, headcounts = {}) {
  return tiers.map((tier) => {
    const phases = {};
    PHASES.forEach((p) => (phases[p.key] = +phaseAverage(scores, tier.key, p.key).toFixed(1)));
    const total = +tierAverage(scores, tier.key).toFixed(1);
    return {
      tier_key: tier.key,
      phases,
      total,
      band: bandKey(total),
      readiness: readinessPct(total),
      headcount: Number(headcounts[tier.key]) || 1,
    };
  });
}

// 360: positive delta = leader rates themselves higher than the team does (blind spot).
export function blindSpots(scores, teamScores, tierKey) {
  return COMPETENCIES.map((c) => ({
    ...c,
    self: scoreOf(scores, tierKey, c.key),
    team: scoreOf(teamScores, tierKey, c.key),
    delta: +(scoreOf(scores, tierKey, c.key) - scoreOf(teamScores, tierKey, c.key)).toFixed(1),
  })).sort((a, b) => b.delta - a.delta);
}

export const hasTeamView = (teamScores, tierKey) => !!teamScores?.[tierKey];
